import { useEffect, useState } from 'react'
import { sha256Hex } from '@/shared/lib/hash'
import {
  AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Loader2, Play, Upload, X,
} from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, Note } from '@/shared/ui/states'
import { Field, Modal } from '@/shared/ui/Modal'
import { toast } from '@/shared/ui/confirm'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { reserveUpload, confirmUpload } from '@/features/geography/api'
import type { VoterImportBatchEntity } from '@/shared/api/types'
import {
  applyImport, cancelImport, importBatch, downloadImportTemplate, mergeVoter, submitImport,
  IMPORT_STATUS_LABEL,
} from './api'

/**
 * Register import.
 *
 * The pipeline is the one `geography` established and is deliberately not a
 * single "upload and go" button:
 *
 *   reserve → presigned PUT → confirm → POST /import {dryRun:true}
 *          → poll → read the report → apply
 *
 * The dry run is the point. A register file is large, arrives more than once and
 * is usually imperfect, so the operator reads counts and rejects *before*
 * anything is written. **A batch parked at `VALIDATED` can be reviewed for as
 * long as it takes** — the backend resets `started_at` on the `VALIDATED →
 * APPLYING` transition and applies its stall cutoff only to `APPLYING`, which is
 * the fix for the Phase 2 defect where a dry run reviewed for over an hour could
 * never be applied. The UI says so, because an operator who does not know that
 * will rush.
 *
 * `apply` and `cancel` are national-only server-side. A scoped caller sees the
 * buttons refuse rather than being hidden, so the refusal is legible.
 */
export function VoterImportWizard() {
  const canImport = useCan('voter:import')

  const [file, setFile] = useState<File | null>(null)
  const [batch, setBatch] = useState<VoterImportBatchEntity | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Poll while the backend is working. `VALIDATED` is a resting state and is not
  // polled — the operator decides when to move, and there is no clock on them.
  useEffect(() => {
    if (!batch) return
    const working = batch.status === 'PENDING' || batch.status === 'VALIDATING'
      || batch.status === 'APPLYING'
    if (!working) return
    const t = setInterval(async () => {
      try { setBatch(await importBatch(batch.id)) } catch { /* transient */ }
    }, 2500)
    return () => clearInterval(t)
  }, [batch])

  const downloadTemplate = async () => {
    setError(null)
    try {
      await downloadImportTemplate()
    } catch (err) { setError(explain(err)) }
  }

  const startDryRun = async () => {
    if (!file) return
    setBusy('upload')
    setError(null)
    try {
      const reserved = await reserveUpload({
        filename: file.name,
        contentType: 'text/csv',
        sizeBytes: file.size,
        purpose: 'VOTER_IMPORT',
      })
      await fetch(reserved.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'text/csv' },
      })
      await confirmUpload(reserved.fileId, await sha256Hex(file))
      setBatch(await submitImport({ fileId: reserved.fileId, dryRun: true, mapping: {} }))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    setError(null)
    try {
      await fn()
      if (batch) setBatch(await importBatch(batch.id))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (!canImport) {
    return (
      <Panel>
        <EmptyState
          title="Import needs `voter:import`"
          hint="It is a dangerous permission — an import writes to the register in bulk."
        />
      </Panel>
    )
  }

  const rejected = batch?.rejectedCount ?? 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Import the register"
        description="Upload, dry run, read the report, then apply. Nothing is written until you apply."
        right={
          <button type="button" className="erp-btn" onClick={() => void downloadTemplate()} aria-label="Template">
            <Download className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Template</span>
          </button>
        }
      />

      <ErrorStrip message={error} />

      {!batch && (
        <Panel>
          <div className="space-y-3">
            <label className="block">
              <span className="text-erp-xs text-muted-foreground">CSV file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="erp-input w-full mt-1"
              />
            </label>
            <p className="text-erp-xs text-muted-foreground">
              Matched on the national-ID blind index, then on <code className="font-mono">(source, externalRef)</code>.
              Re-running the same file changes nothing and reports every row unchanged.
              Rows implying a person under 18 at the next general election are rejected, not warned.
            </p>
            <button
              type="button"
              className="erp-btn erp-btn-primary"
              disabled={!file || busy !== null}
              onClick={() => void startDryRun()}
            >
              {busy === 'upload'
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
                : <><Upload className="h-3.5 w-3.5" /> Upload and dry run</>}
            </button>
          </div>
        </Panel>
      )}

      {batch && (
        <Panel
          title={`Batch ${batch.id.slice(0, 8)}`}
          description={IMPORT_STATUS_LABEL[batch.status] ?? batch.status}
          right={
            <button type="button" className="erp-btn" onClick={() => { setBatch(null); setFile(null) }}>
              <X className="h-3.5 w-3.5" /> New import
            </button>
          }
        >
          <div className="space-y-3">
            {batch.status === 'VALIDATED' && (
              <Note tone="info">
                <span className="inline-flex items-start gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Validated and waiting. <strong>Take as long as you need</strong> — the
                    apply clock starts when you press Apply, not when the dry run finished.
                  </span>
                </span>
              </Note>
            )}
            {batch.status === 'FAILED' && batch.error && <Note tone="bad">{batch.error}</Note>}

            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <Count label="Total" value={batch.totalRows} />
              <Count label="Processed" value={batch.processedRows} />
              <Count label="Create" value={batch.createdCount} tone="good" />
              <Count label="Update" value={batch.updatedCount} />
              <Count label="Duplicate" value={batch.duplicateCount} tone={batch.duplicateCount ? 'warn' : undefined} />
              <Count label="Rejected" value={batch.rejectedCount} tone={rejected ? 'bad' : undefined} />
            </div>

            {rejected > 0 && (
              <Note tone="bad">
                <span className="inline-flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    {fmtNumber(rejected)} row{rejected === 1 ? '' : 's'} rejected. Applying
                    imports the valid rows and leaves these out — fix and re-import them,
                    or apply and accept the gap deliberately.
                  </span>
                </span>
              </Note>
            )}

            <dl className="text-erp-xs text-muted-foreground space-y-0.5">
              <div>Validated {fmtDateTime(batch.validatedAt) || '—'}</div>
              <div>Applied {fmtDateTime(batch.startedAt) || '—'}</div>
              <div>Finished {fmtDateTime(batch.finishedAt) || '—'}</div>
            </dl>

            {(batch.status === 'PENDING' || batch.status === 'VALIDATING' || batch.status === 'APPLYING') && (
              <div className="text-erp-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {batch.status === 'APPLYING' ? 'Applying, chunk by chunk…' : 'Validating…'}
              </div>
            )}

            {batch.status === 'VALIDATED' && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="erp-btn erp-btn-primary"
                  disabled={busy !== null}
                  onClick={() => void act('apply', () => applyImport(batch.id))}
                >
                  {busy === 'apply'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Play className="h-3.5 w-3.5" />}
                  Apply {fmtNumber((batch.createdCount ?? 0) + (batch.updatedCount ?? 0))} rows
                </button>
                <button
                  type="button"
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => void act('cancel', () => cancelImport(batch.id))}
                >
                  Cancel batch
                </button>
              </div>
            )}
            {batch.status === 'VALIDATED' && (
              <p className="text-erp-xs text-muted-foreground">
                Apply and cancel are national-only. A scoped account will be refused here
                rather than silently ignored.
              </p>
            )}
          </div>
        </Panel>
      )}
    </div>
  )
}

function Count({
  label, value, tone,
}: { label: string; value?: number | null; tone?: 'good' | 'warn' | 'bad' }) {
  return (
    <div className="erp-card px-3 py-2">
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn(
        'text-erp-lg font-semibold erp-metric tabular-nums',
        tone === 'good' && 'text-emerald-700 dark:text-emerald-400',
        tone === 'warn' && 'text-amber-700 dark:text-amber-400',
        tone === 'bad' && 'text-red-700 dark:text-red-400',
      )}>
        {value == null ? '—' : fmtNumber(value)}
      </div>
    </div>
  )
}

/**
 * Duplicate review.
 *
 * Nothing is ever merged automatically — the backend scores candidate pairs and
 * parks them here, and a merge needs `confirm: true`. So this view previews
 * first and only then commits, and the preview call writes nothing.
 */
/**
 * One candidate pair, previewed and then merged.
 *
 * The preview call is the same endpoint with `confirm: false`, so what you read here is
 * what the server will actually do rather than a client-side guess. Which record survives
 * is a choice, not a default: the higher id is not the better record, and the pair is
 * usually two halves of one person where only a human knows which half is right.
 */
function MergeDialog({ pair, onClose, onMerged }: {
  pair: { voterAId: string; voterBId: string; score: number } | null
  onClose: () => void
  onMerged: () => void
}) {
  const [survivor, setSurvivor] = useState<'a' | 'b'>('a')
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const survivingId = pair ? (survivor === 'a' ? pair.voterAId : pair.voterBId) : ''
  const mergedId = pair ? (survivor === 'a' ? pair.voterBId : pair.voterAId) : ''

  useEffect(() => {
    if (!pair) { setPreview(null); setError(null); return }
    let alive = true
    setError(null)
    // confirm:false — this writes nothing, and it is how the names get resolved.
    mergeVoter(mergedId, survivingId, false)
      .then((p) => { if (alive) setPreview(p) })
      .catch((err) => { if (alive) setError(explain(err)) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, survivor])

  const conflicts = (preview?.conflicts as string[] | undefined) ?? []
  const survivingName = String(preview?.survivingDisplayName ?? '…')
  const mergedName = String(preview?.mergedDisplayName ?? '…')

  async function commit() {
    setBusy(true); setError(null)
    try {
      await mergeVoter(mergedId, survivingId, true)
      toast('good', 'Records merged')
      onMerged()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={Boolean(pair)}
      onClose={onClose}
      title="Merge two records"
      description="One record survives and keeps the union of both histories. The other is marked merged — it is not deleted, so the decision stays auditable."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary"
                  disabled={busy || !preview} onClick={() => { void commit() }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Merge into {survivingName}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <Field label="Which record survives?" required
               hint="The survivor keeps its own identity fields. The other one's contacts, tags, verifications and turnout are repointed onto it.">
          <div className="grid gap-2 sm:grid-cols-2">
            {(['a', 'b'] as const).map((side) => {
              const isSurvivor = survivor === side
              const name = isSurvivor ? survivingName : mergedName
              return (
                <button
                  key={side}
                  type="button"
                  onClick={() => setSurvivor(side)}
                  className={cn(
                    'text-left px-3 py-2 rounded-md border text-erp-sm',
                    isSurvivor
                      ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                      : 'border-input text-muted-foreground',
                  )}
                >
                  <span className="block">{name}</span>
                  <span className="block text-erp-xs opacity-70">
                    {isSurvivor ? 'survives' : 'merged away'}
                  </span>
                </button>
              )
            })}
          </div>
        </Field>

        {conflicts.length > 0 ? (
          <Note tone="info">
            These fields disagree between the two records and the survivor's value wins:{' '}
            <b>{conflicts.join(', ')}</b>. Check the survivor is the one with the right
            value before merging.
          </Note>
        ) : preview ? (
          <Note tone="info">
            No conflicting fields. The two records agree wherever both have a value.
          </Note>
        ) : null}
      </div>
    </Modal>
  )
}

export function DuplicateReview({ candidates, onMerged }: {
  candidates: { id: string; voterAId: string; voterBId: string; score: number
    signals: Record<string, unknown>; reviewStatus: string }[]
  onMerged: () => void
}) {
  const [pair, setPair] = useState<
    { voterAId: string; voterBId: string; score: number } | null>(null)
  return (
    <Panel bodyClassName="p-0">
      <div className="px-4 py-2.5 border-b border-border text-erp-xs text-muted-foreground">
        Scored candidate pairs. Nothing here has been merged — a merge is a deliberate,
        reversible act and needs an explicit confirmation.
      </div>
      <ErpTable head={<>
        <Th>Score</Th>
        <Th>Pair</Th>
        <Th className="axis-hide-md">Signals</Th>
        <Th>Status</Th>
        <Th />
      </>}>
        {candidates.length === 0 && (
          <tr>
            <td colSpan={5} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
              No duplicate candidates pending review.
            </td>
          </tr>
        )}
        {candidates.map((c) => (
          <tr key={c.id} className="hover:bg-muted/40">
            <Td className="erp-metric tabular-nums">{Number(c.score).toFixed(0)}</Td>
            <Td className="font-mono text-erp-xs">
              {c.voterAId.slice(0, 8)} ↔ {c.voterBId.slice(0, 8)}
            </Td>
            <Td className="axis-hide-md text-erp-xs text-muted-foreground truncate max-w-[18rem]">
              {Object.keys(c.signals ?? {}).join(', ') || '—'}
            </Td>
            <Td className="text-erp-xs">{c.reviewStatus.toLowerCase()}</Td>
            <Td className="text-right">
              <button type="button" className="erp-btn"
                      onClick={() => setPair({
                        voterAId: c.voterAId, voterBId: c.voterBId, score: c.score,
                      })}>
                Review
              </button>
            </Td>
          </tr>
        ))}
      </ErpTable>

      <MergeDialog
        pair={pair}
        onClose={() => setPair(null)}
        onMerged={onMerged}
      />
    </Panel>
  )
}

export { FileSpreadsheet }
